import { useEffect, useMemo, useState } from 'react';

export const GRANT_EXPIRING_SOON_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const parseGrantEndsAt = (endsAt?: string | null) => {
  if (!endsAt) {
    return null;
  }

  const parsed = new Date(endsAt);
  const timestamp = parsed.getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }

  return timestamp;
};

export const isGrantExpiringSoonAt = (
  endsAt: string | null | undefined,
  nowMs: number,
  disabled = false
) => {
  if (disabled) {
    return false;
  }

  const endsAtMs = parseGrantEndsAt(endsAt);
  if (endsAtMs === null) {
    return false;
  }

  const msRemaining = endsAtMs - nowMs;
  return msRemaining > 0 && msRemaining <= GRANT_EXPIRING_SOON_WINDOW_MS;
};

const getNextGrantExpiringSoonRefreshDelay = (
  endsAt: string | null | undefined,
  nowMs: number,
  disabled = false
) => {
  if (disabled) {
    return null;
  }

  const endsAtMs = parseGrantEndsAt(endsAt);
  if (endsAtMs === null) {
    return null;
  }

  const msUntilWindowStart = endsAtMs - GRANT_EXPIRING_SOON_WINDOW_MS - nowMs;
  if (msUntilWindowStart > 0) {
    return msUntilWindowStart;
  }

  const msUntilExpiry = endsAtMs - nowMs;
  if (msUntilExpiry > 0) {
    return msUntilExpiry;
  }

  return null;
};

export function useGrantExpiringSoon(endsAt?: string | null, disabled = false) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    setNowMs(Date.now());
  }, [endsAt, disabled]);

  const expiringSoon = useMemo(
    () => isGrantExpiringSoonAt(endsAt, nowMs, disabled),
    [endsAt, nowMs, disabled]
  );
  const nextRefreshDelay = useMemo(
    () => getNextGrantExpiringSoonRefreshDelay(endsAt, nowMs, disabled),
    [endsAt, nowMs, disabled]
  );

  useEffect(() => {
    if (nextRefreshDelay === null) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setNowMs(Date.now());
    }, nextRefreshDelay);

    return () => window.clearTimeout(timeoutId);
  }, [nextRefreshDelay]);

  return expiringSoon;
}
